/**
 * Payment Method Selector Component
 * Allows users to select payment method (Card, Apple Pay, Google Pay, ACH, Cash App)
 */

import React, { useState, useEffect } from 'react';
import { CreditCard, Building2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';
import { SavedPaymentMethods } from './SavedPaymentMethods';
import { GooglePayButton } from './GooglePayButton';
import { ApplePayButton } from './ApplePayButton';

interface PaymentMethodOption {
  id: string;
  type: 'card' | 'apple_pay' | 'google_pay' | 'ach' | 'coinflow_iframe' | 'cashapp' | 'crypto';
  displayName: string;
  icon: React.ReactNode;
  description: string;
  processors: string[];
  iconBgColor?: string;
}

interface PaymentMethodSelectorProps {
  onSelect: (method: PaymentMethodOption['type'], processorId: string) => void;
  onSelectSavedCard?: (savedCard: any) => void; // Callback for when a saved card is selected
  packageId: string;
  scheduledOfferId?: string;  // For scheduled offer purchases
  amount: number;
  refreshKey?: number; // Key to trigger refresh of saved cards
  packageTitle?: string; // Package title for Google Pay and Apple Pay
  onGooglePaySuccess?: (paymentId: string) => void; // Callback for Google Pay success
  onGooglePayError?: (error: string | Error) => void; // Callback for Google Pay error
  onApplePaySuccess?: (paymentId: string) => void; // Callback for Apple Pay success
  onApplePayError?: (error: string | Error) => void; // Callback for Apple Pay error
  /** Checkout session transaction ID - shared across all payment methods */
  checkoutSessionId?: number | null;
  /** Callback for Cash App checkout - initiates Cash App payment flow */
  onCashAppCheckout?: () => void;
  /** Callback for Crypto checkout - opens hosted checkout with crypto only */
  onCryptoCheckout?: () => void;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  onSelect,
  onSelectSavedCard,
  packageId,
  scheduledOfferId,
  amount,
  refreshKey = 0,
  packageTitle = 'Package',
  onGooglePaySuccess,
  onGooglePayError,
  onApplePaySuccess,
  onApplePayError,
  checkoutSessionId,
  onCashAppCheckout,
  onCryptoCheckout,
}) => {
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasSavedCards, setHasSavedCards] = useState(false);
  const [isGooglePayMethodAvailable, setIsGooglePayMethodAvailable] = useState(false);
  const [isApplePayMethodAvailable, setIsApplePayMethodAvailable] = useState(false);

  useEffect(() => {
    // Check Apple Pay availability
    const checkApplePay = () => {
      if (typeof window !== 'undefined' && (window as any).ApplePaySession) {
        try {
          const canMakePayments = (window as any).ApplePaySession.canMakePayments();
          setIsApplePayAvailable(canMakePayments);
        } catch (error) {
          setIsApplePayAvailable(false);
        }
      } else {
        setIsApplePayAvailable(false);
      }
    };

    // Check Google Pay availability (load script if needed)
    const checkGooglePay = () => {
      if (typeof window === 'undefined') {
        setIsGooglePayAvailable(false);
        return;
      }

      // Check if already loaded
      if ((window as any).google?.payments?.api?.PaymentsClient) {
        // Google Pay is loaded, check if ready
        try {
          const client = new (window as any).google.payments.api.PaymentsClient({
            environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
          });

          client
            .isReadyToPay({
              apiVersion: 2,
              apiVersionMinor: 0,
              allowedPaymentMethods: [
                {
                  type: 'CARD',
                  parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: ['VISA', 'MASTERCARD'],
                  },
                },
              ],
            })
            .then((response: any) => {
              setIsGooglePayAvailable(response.result || false);
            })
            .catch(() => {
              setIsGooglePayAvailable(false);
            });
        } catch (error) {
          setIsGooglePayAvailable(false);
        }
      } else {
        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src="https://pay.google.com/gp/p/pay.js"]');
        if (existingScript) {
          // Script is already loading, wait for it
          existingScript.addEventListener('load', () => {
            if ((window as any).google?.payments?.api?.PaymentsClient) {
              try {
                const client = new (window as any).google.payments.api.PaymentsClient({
                  environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
                });

                client
                  .isReadyToPay({
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                      {
                        type: 'CARD',
                        parameters: {
                          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                          allowedCardNetworks: ['VISA', 'MASTERCARD'],
                        },
                      },
                    ],
                  })
                  .then((response: any) => {
                    setIsGooglePayAvailable(response.result || false);
                  })
                  .catch(() => {
                    setIsGooglePayAvailable(false);
                  });
              } catch (error) {
                setIsGooglePayAvailable(false);
              }
            } else {
              setIsGooglePayAvailable(false);
            }
          });
          return;
        }

        // Load Google Pay script
        const script = document.createElement('script');
        script.src = 'https://pay.google.com/gp/p/pay.js';
        script.async = true;
        script.onload = () => {
          if ((window as any).google?.payments?.api?.PaymentsClient) {
            try {
              const client = new (window as any).google.payments.api.PaymentsClient({
                environment: import.meta.env.VITE_COINFLOW_ENVIRONMENT === 'production' ? 'PRODUCTION' : 'TEST',
              });

              client
                .isReadyToPay({
                  apiVersion: 2,
                  apiVersionMinor: 0,
                  allowedPaymentMethods: [
                    {
                      type: 'CARD',
                      parameters: {
                        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                        allowedCardNetworks: ['VISA', 'MASTERCARD'],
                      },
                    },
                  ],
                })
                .then((response: any) => {
                  setIsGooglePayAvailable(response.result || false);
                })
                .catch(() => {
                  setIsGooglePayAvailable(false);
                });
            } catch (error) {
              setIsGooglePayAvailable(false);
            }
          } else {
            setIsGooglePayAvailable(false);
          }
        };
        script.onerror = () => {
          setIsGooglePayAvailable(false);
        };
        document.head.appendChild(script);
      }
    };

    // Check Apple Pay first (synchronous)
    checkApplePay();

    // Check Google Pay (may be async)
    checkGooglePay();
  }, []);

  // Refetch methods whenever availability changes
  useEffect(() => {
    fetchAvailableMethods(isApplePayAvailable, isGooglePayAvailable);
  }, [isApplePayAvailable, isGooglePayAvailable]);

  const fetchAvailableMethods = async (applePayAvailable: boolean, googlePayAvailable: boolean) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/payments/available-methods');
      const availableMethodsData = response.methods || [];
      const methodProcessors = response.methodProcessors || {};

      // Build method options from available methods
      const availableMethods: PaymentMethodOption[] = [];

      // Card
      if (availableMethodsData.includes('card')) {
        availableMethods.push({
          id: 'card',
          type: 'card',
          displayName: 'Credit or Debit Card',
          icon: <CreditCard className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Visa, Mastercard',
          processors: methodProcessors['card'] || [],
        });
      }

      // Apple Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list

      // Google Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list
      setIsGooglePayMethodAvailable(availableMethodsData.includes('google_pay'));
      
      // Apple Pay - Not added to methods array since we render it as a button directly
      // We check availability but don't add it to the methods list
      setIsApplePayMethodAvailable(availableMethodsData.includes('apple_pay'));

      // Credit & Debit Card (Coinflow Hosted Checkout)
      // Shows when coinflow_hosted method is enabled in payment processor config
      if (availableMethodsData.includes('coinflow_hosted')) {
        availableMethods.push({
          id: 'coinflow_iframe',
          type: 'coinflow_iframe',
          displayName: 'Credit & Debit Card',
          icon: (
            <img 
              src="/images/payment-methods-grid.webp" 
              alt="Visa, Mastercard, Apple Pay, Google Pay" 
              className="w-11 h-11 lg:w-14 lg:h-14 rounded-lg object-contain"
            />
          ),
          description: 'Visa, Mastercard, Apple Pay, Google Pay',
          processors: methodProcessors['coinflow_hosted'] || ['coinflow'],
          iconBgColor: 'bg-transparent',
        });
      }

      // ACH
      if (availableMethodsData.includes('ach')) {
        availableMethods.push({
          id: 'ach',
          type: 'ach',
          displayName: 'Bank Transfer (ACH)',
          icon: (
            <img 
              src="/images/bank-ach-logo.webp" 
              alt="Bank Transfer" 
              className="w-11 h-11 lg:w-14 lg:h-14 rounded-lg object-contain"
            />
          ),
          description: 'Lower fees, 2-3 business days',
          processors: methodProcessors['ach'] || [],
          iconBgColor: 'bg-transparent',
        });
      }

      // Crypto - Always show as an option (coming soon if not enabled or no handler)
      const cryptoEnabled = availableMethodsData.includes('crypto');
      const cryptoAvailable = cryptoEnabled && !!onCryptoCheckout;
      availableMethods.push({
        id: 'crypto',
        type: 'crypto',
        displayName: 'Crypto',
        icon: (
          <img 
            src="/images/crypto-methods-grid.webp" 
            alt="Bitcoin, Ethereum, and other cryptocurrencies" 
            className="w-11 h-11 lg:w-14 lg:h-14 rounded-lg object-contain"
          />
        ),
        description: cryptoAvailable ? 'Pay with cryptocurrency' : 'Coming soon',
        processors: methodProcessors['crypto'] || ['coinflow'],
        iconBgColor: 'bg-transparent',
      });

      // Cash App - Always show as an option (coming soon if not enabled or no handler)
      const cashAppEnabled = availableMethodsData.includes('cashapp');
      const cashAppAvailable = cashAppEnabled && !!onCashAppCheckout;
      availableMethods.push({
        id: 'cashapp',
        type: 'cashapp',
        displayName: 'Cash App',
        icon: (
          <img 
            src="/images/cashapp-logo.webp" 
            alt="Cash App" 
            className="w-11 h-11 lg:w-14 lg:h-14 rounded-lg object-contain"
          />
        ),
        description: cashAppAvailable ? 'Pay with Cash App' : 'Coming soon',
        processors: methodProcessors['cashapp'] || ['cashapp'],
        iconBgColor: 'bg-transparent',
      });

      setMethods(availableMethods);
    } catch {
      // Fallback to default methods if API fails
      setMethods([
        {
          id: 'card',
          type: 'card',
          displayName: 'Credit or Debit Card',
          icon: <CreditCard className="h-4 w-4 lg:h-6 lg:w-6" />,
          description: 'Visa, Mastercard',
          processors: ['coinflow'],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodSelect = (method: PaymentMethodOption) => {
    // Handle Cash App separately - call the Cash App checkout flow
    if (method.type === 'cashapp' && onCashAppCheckout) {
      setSelectedMethod(method.id);
      onCashAppCheckout();
      return;
    }
    
    // Handle Crypto separately - call the Crypto checkout flow
    if (method.type === 'crypto' && onCryptoCheckout) {
      setSelectedMethod(method.id);
      onCryptoCheckout();
      return;
    }
    
    setSelectedMethod(method.id);
    // Select first available processor (for now, just Coinflow)
    onSelect(method.type, method.processors[0]);
  };

  const handleSavedCardSelect = (savedCard: any) => {
    if (onSelectSavedCard) {
      onSelectSavedCard(savedCard);
    }
  };

  // Check if user has saved cards - refresh when refreshKey changes
  useEffect(() => {
    const checkSavedCards = async () => {
      try {
        const response = await apiClient.get('/payments/methods');
        setHasSavedCards(response && response.length > 0);
      } catch {
        // If error, assume no saved cards
        setHasSavedCards(false);
      }
    };
    checkSavedCards();
  }, [refreshKey]); // Refresh when refreshKey changes

  if (loading) {
    return (
      <div className="space-y-2 lg:space-y-4">
        <div className="text-center py-4 lg:py-8 text-muted-foreground">
          <div className="animate-spin rounded-full h-5 w-5 lg:h-8 lg:w-8 border-2 border-primary border-t-transparent mx-auto mb-2"></div>
          <p className="text-[10px] lg:text-sm font-medium">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="space-y-2 lg:space-y-4">
        <div className="text-center py-4 lg:py-8 text-muted-foreground border border-dashed border-border rounded-lg">
          <p className="font-black text-[10px] lg:text-sm mb-1 lg:mb-2">No payment methods available</p>
          <p className="text-[9px] lg:text-sm">
            Please contact support or try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-5">
      {/* Show saved cards if available */}
      {hasSavedCards && (
        <div className="space-y-2.5 lg:space-y-3">
          <h4 className="text-[10px] lg:text-sm font-medium text-white/60 uppercase tracking-wider">Saved Cards</h4>
          <SavedPaymentMethods
            onSelect={handleSavedCardSelect}
            showSelectButton={true}
            refreshKey={refreshKey}
            showHeading={false}
          />
        </div>
      )}

      {/* Available payment methods */}
      <div className="space-y-2 lg:space-y-3">
        <h4 className="text-[10px] lg:text-sm font-medium text-white/60 uppercase tracking-wider">
          {hasSavedCards ? 'Other Payment Methods' : 'Select a payment method'}
        </h4>
        <div className="space-y-2.5 lg:space-y-3">
          {methods.map((method) => {
            const isComingSoon = method.description === 'Coming soon';
            return (
              <button
                key={method.id}
                disabled={isComingSoon}
                className={cn(
                  'w-full flex items-center gap-3 lg:gap-4 p-3.5 lg:p-4 rounded-2xl transition-all duration-300',
                  'backdrop-blur-md',
                  isComingSoon 
                    ? 'bg-white/[0.02] border border-white/[0.05] cursor-not-allowed opacity-60'
                    : 'bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/20',
                  selectedMethod === method.id && !isComingSoon && 'border-primary/60 bg-primary/10 ring-1 ring-primary/30 shadow-lg shadow-primary/10',
                )}
                onClick={() => !isComingSoon && handleMethodSelect(method)}
              >
                {/* Icon Container */}
                <div className={cn(
                  'flex-shrink-0 w-11 h-11 lg:w-14 lg:h-14 rounded-xl flex items-center justify-center backdrop-blur-sm',
                  method.iconBgColor || 'bg-white/[0.05]'
                )}>
                  {method.icon}
                </div>
                
                {/* Text Content */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-sm lg:text-base text-white truncate">
                      {method.displayName}
                    </h4>
                    {isComingSoon && (
                      <span className="text-[9px] lg:text-[10px] font-medium text-amber-400/80 bg-amber-400/10 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                        Soon
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] lg:text-xs text-white/50 truncate">
                    {isComingSoon ? 'Pay with Cash App' : method.description}
                  </p>
                </div>

                {/* Selection Indicator */}
                {!isComingSoon && (
                  <div className={cn(
                    'flex-shrink-0 w-6 h-6 lg:w-7 lg:h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300',
                    selectedMethod === method.id 
                      ? 'border-primary bg-primary shadow-lg shadow-primary/30' 
                      : 'border-white/20 bg-white/[0.03]'
                  )}>
                    {selectedMethod === method.id && (
                      <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-white" />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Apple Pay and Google Pay buttons - separate section at bottom */}
      {/* Google Pay TEMPORARILY DISABLED - only show section if Apple Pay is available */}
      {(isApplePayMethodAvailable && isApplePayAvailable && onApplePaySuccess && onApplePayError) ? (
        <div className="pt-2 lg:pt-4 border-t border-border space-y-2 lg:space-y-3">
          <div
            className={cn(
              'grid gap-2 lg:gap-4',
              'grid-cols-1'
            )}
          >
            {/* Apple Pay button */}
            {isApplePayMethodAvailable && isApplePayAvailable && onApplePaySuccess && onApplePayError && (
              <div className="flex flex-col">
                <ApplePayButton
                  amount={amount}
                  packageId={packageId}
                  scheduledOfferId={scheduledOfferId}
                  packageTitle={packageTitle}
                  merchantId={import.meta.env.VITE_COINFLOW_MERCHANT_ID || ''}
                  onSuccess={onApplePaySuccess}
                  onError={onApplePayError}
                  checkoutSessionId={checkoutSessionId}
                />
              </div>
            )}

            {/* Google Pay button - TEMPORARILY DISABLED for testing */}
            {/* TODO: Re-enable once Coinflow Google Pay issue is resolved */}
            {false && isGooglePayMethodAvailable && isGooglePayAvailable && onGooglePaySuccess && onGooglePayError && (
              <div className="flex flex-col">
                <GooglePayButton
                  amount={amount}
                  packageId={packageId}
                  scheduledOfferId={scheduledOfferId}
                  packageTitle={packageTitle}
                  merchantId={import.meta.env.VITE_COINFLOW_MERCHANT_ID || ''}
                  onSuccess={onGooglePaySuccess}
                  onError={onGooglePayError}
                  checkoutSessionId={checkoutSessionId}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

